import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  model,
  OnInit,
  output,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { catchError, combineLatest, EMPTY, finalize, merge, Observable, of, startWith, Subject, switchMap, tap } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, take } from 'rxjs/operators';
import { AddressSearchLogic } from '@mwe/services';
import { NgbHighlight, NgbTypeahead, NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';
import { AddressInitialPart, AddressParts, FormFieldId, IOlavEntry, ISolrDocList, SearchableFieldId } from '@mwe/models';
import {
  buildTypeaheadValidator,
  fieldFlow,
  formatValue,
  getBaseValidators,
  isNoResultsMessage,
  searchableFields,
  shouldEnableDoor,
} from './utils/utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgClass } from '@angular/common';
import { markFormGroupDirty } from '@mwe/utils';
import { Options } from '@popperjs/core';
import { InlineNotificationComponent } from '../inline-notification/inline-notification.component';
import { zipCodeValidator } from '../form/validators/zip-code.validator';

const DEBOUNCE_TIME = 250;

@Component({
  selector: 'mwe-address-search',
  imports: [ReactiveFormsModule, NgbTypeahead, TranslatePipe, NgbHighlight, NgClass, InlineNotificationComponent],
  templateUrl: './address-search.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AddressSearchLogic],
  encapsulation: ViewEncapsulation.None,
  styles: `
    .max-height-200 {
      max-height: 200px;
      overflow-y: auto;
    }
  `,
})
export class AddressSearchComponent implements OnInit {
  readonly predefinedOlavKey = input<string>('');
  readonly ignoreAvailableZipCodes = input<boolean>(false);
  readonly processType = input<string>(null);
  readonly addressInputCompleted = output<boolean>();
  readonly noResultsFound = output<boolean>();
  readonly selectedOlav = model<IOlavEntry | null>(null);
  readonly showError = model<boolean>(false);
  readonly hasNoResults = signal<boolean>(false);
  readonly inputContext = signal<FormFieldId | null>(null);
  readonly isLoading = signal<FormFieldId | string>('');
  readonly searchFields: Record<string, (text$: Observable<string>) => Observable<readonly any[]>> = {};
  readonly fieldIDs: FormFieldId[][] = [
    ['zipCode', 'location'],
    ['street', 'building', 'door'],
  ];
  readonly selection = computed<IOlavEntry>(() => {
    const context = this.inputContext();
    const selectedOlav = this.selectedOlav();

    const inputStr = document.querySelector<HTMLInputElement>(`#${this.inputContext()}`)?.value ?? '';

    const customSelectedOlav = {} as IOlavEntry;

    const replaceSlash = (val: any) => (typeof val === 'string' ? val.replace(/\\/g, '/') : val);

    switch (context) {
      case 'zipCode':
      case 'location':
        customSelectedOlav.plz = inputStr;
        break;
      case 'street':
        customSelectedOlav.plz = selectedOlav?.plz;
        customSelectedOlav.gemeinde = selectedOlav?.gemeinde;
        customSelectedOlav.strasse = inputStr;
        break;
      case 'building':
        customSelectedOlav.plz = selectedOlav?.plz;
        customSelectedOlav.gemeinde = selectedOlav?.gemeinde;
        customSelectedOlav.strasse = selectedOlav?.strasse;
        customSelectedOlav.gebadr1 = replaceSlash(inputStr);
        break;
      case 'door':
        customSelectedOlav.plz = selectedOlav?.plz;
        customSelectedOlav.gemeinde = selectedOlav?.gemeinde;
        customSelectedOlav.strasse = selectedOlav?.strasse;
        customSelectedOlav.gebadr1 = selectedOlav?.gebadr1;
        customSelectedOlav.tuerNr = replaceSlash(inputStr);
        break;
    }

    return customSelectedOlav;
  });
  readonly formatters: Record<string, (input: any) => string> = {};
  private readonly addressSearchLogic = inject(AddressSearchLogic);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);
  readonly formGroup = this.fb.group({
    zipCode: [undefined, [Validators.required]],
    location: [undefined],
    street: [{ value: '', disabled: true }],
    building: [{ value: '', disabled: true }],
    door: [{ value: null, disabled: true }],
  });
  private readonly useIdentSearch = computed(() => {
    const selectedOlav = this.selectedOlav();
    const context = this.inputContext();

    // Only apply useIdentSearch logic when searching for door field
    if (context !== 'door' || !selectedOlav) {
      return false;
    }

    // Same logic as in address-input.component.ts
    return !!selectedOlav.identAdresse || selectedOlav.strasse !== selectedOlav.identStrasse;
  });
  private readonly validatedFields = new Set<FormFieldId>();
  private hasHandledPreselectedOlav = false;
  private optionalDoorEntry = signal<IOlavEntry>(null);

  focusClickContents$ = new Subject<{ value: string; fieldId: FormFieldId }>();

  constructor() {
    this.search = this.search.bind(this);
    this.formatter = this.formatter.bind(this);

    this.fieldIDs.flat().forEach(id => {
      this.formatters[id] = (input: any) => this.formatter(input, id);
      this.searchFields[id] = (text$: Observable<string>) => this.search(text$, id as FormFieldId);
    });

    this.handleZipCodeChanges();

    effect(() => {
      this.handlePredefinedOlav();
    });

    effect(() => {
      this.handlePreselectedOlav();
    });
  }

  ngOnInit() {
    this.formGroup.get('zipCode').addValidators(zipCodeValidator(this.ignoreAvailableZipCodes()));
  }

  search(text$: Observable<string>, fieldId: FormFieldId) {
    this.hasHandledPreselectedOlav = true;

    const value$ = merge(
      text$.pipe(map(value => ({ value, fieldId }))),
      this.focusClickContents$.pipe(filter(f => f.fieldId === fieldId)),
    ).pipe(
      map(f => f.value),
      distinctUntilChanged(),
      filter(i => i != null),
    );

    return combineLatest([value$, this.formGroup.valueChanges.pipe(startWith({}))]).pipe(
      takeUntilDestroyed(this.destroyRef),
      debounceTime(DEBOUNCE_TIME),
      distinctUntilChanged((previous, current) => {
        return previous[0] === current[0] && JSON.stringify(previous[1]) === JSON.stringify(current[1]);
      }),
      switchMap(input => {
        const currentFormValues$ = of(this.formGroup.value);
        const currentFormInput$ = of(input[1]);

        return merge(currentFormValues$, currentFormInput$).pipe(
          take(1),
          map(formatValues => [input[0], formatValues] as const),
        );
      }),
      switchMap(([textInput, formValues]) => {
        if (this.inputContext() === 'zipCode') {
          const control = this.formGroup.get('zipCode');
          if (control?.errors?.['zipAvailability']) {
            this.isLoading.set('');
            this.hasNoResults.set(false);
            return of([]);
          }
        }
        if (!this.isValidSearchInput(textInput, formValues, fieldId)) {
          this.isLoading.set('');
          this.hasNoResults.set(false);
          return of([]);
        }
        const addressParts = this.buildAddressParts(formValues, textInput, fieldId);
        this.isLoading.set(fieldId);
        this.showError.set(false);
        this.hasNoResults.set(false);

        return (
          this.addressSearchLogic.search({
            value: addressParts,
            isIdentSearch: this.useIdentSearch(),
            ignoreAvailableZipCodes: this.ignoreAvailableZipCodes(),
          }) ?? EMPTY
        ).pipe(
          tap((results: ISolrDocList[]) => {
            const hasNoResults = !results?.length || (results.length === 1 && isNoResultsMessage(results[0]));
            this.hasNoResults.set(hasNoResults);
          }),
          finalize(() => this.isLoading.set('')),
        );
      }),
      catchError(() => {
        this.isLoading.set('');
        this.showError.set(true);
        this.hasNoResults.set(false);
        return of([]);
      }),
    );
  }

  setFocusAndClick(event: Event, field: FormFieldId) {
    this.setContext(field);
    const value = (event?.target?.['value'] as string) ?? '';
    if (event.isTrusted && value.length > 0) {
      this.focusClickContents$.next({ value, fieldId: field });
    }
  }

  onSelectItem({ item }: NgbTypeaheadSelectItemEvent<ISolrDocList>) {
    const context = this.inputContext();
    if (context) {
      this.addValidatorsAfterSelection(context);
      this.validatedFields.add(context);

      const fieldSequence: FormFieldId[] = ['zipCode', 'location', 'street', 'building', 'door'];
      const currentIndex = fieldSequence.indexOf(context);
      const nextFieldToClear = fieldSequence[currentIndex + 1];
      if (nextFieldToClear) {
        this.clearFollowingInputs(nextFieldToClear);
      }
    }

    if (isNoResultsMessage(item)) {
      return;
    }

    const selectedEntry = item.docs?.[0];
    if (!selectedEntry) return;

    this.selectedOlav.set(selectedEntry);

    this.updateFormWithSelection(item);
    this.enableNextField();

    // Only check completion after the full selection flow is complete
    setTimeout(() => this.checkAddressCompletion(), 0);
  }

  onKeydown(event: KeyboardEvent) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return;
    }

    // select the active element option and scroll it into view after DOM updates
    window.requestAnimationFrame(() => {
      const win = document.querySelector('ngb-typeahead-window');
      if (!win) return;

      const active = win.querySelector('.active');
      if (!active) return;

      active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
  }

  validate() {
    const control = this.formGroup.get('door');
    if (!this.formatter(control.value, 'door')?.trim() && this.optionalDoorEntry()) {
      this.selectedOlav.set(this.optionalDoorEntry());
    }

    markFormGroupDirty(this.formGroup);

    // An address search form is only valid if the FormGroup is valid
    // AND all required enabled fields have been explicitly validated via selection
    const requiredFields: FormFieldId[] = ['zipCode', 'street', 'building'];
    if (this.formGroup.get('door')?.enabled && !this.optionalDoorEntry()) {
      requiredFields.push('door');
    }

    const allSelectionsMade = requiredFields.every(field => this.validatedFields.has(field));
    requiredFields
      .filter(field => !this.validatedFields.has(field))
      .forEach(field => {
        const control = this.formGroup.get(field);

        if (!control?.enabled) return;
        control?.setErrors({ empty: true });
      });

    return this.formGroup.valid && allSelectionsMade;
  }

  isControlEnabled(name: FormFieldId) {
    const control = this.formGroup.get(name);
    return control?.enabled && !this.optionalDoorEntry();
  }

  setContext(context: FormFieldId) {
    if (context === 'location') return;
    this.inputContext.set(context);
  }

  resultFormatter(input: ISolrDocList, context: FormFieldId = this.inputContext()) {
    // If it's a no result message, return it as-is
    if (isNoResultsMessage(input)) {
      return Array.isArray(input) ? input[0] : input;
    }

    return formatValue(input, 'result', context);
  }

  formatter(input: ISolrDocList, context: FormFieldId = this.inputContext()) {
    // If no result is found, return field contents as is
    const formattedInput = formatValue(input, 'input', context);
    if (formattedInput?.length) return formattedInput;

    const currentInputContents = this.formGroup.get(context)?.value;

    if (typeof currentInputContents !== 'string' || !currentInputContents || this.predefinedOlavKey()?.length) return '';
    return currentInputContents;
  }

  readonly getLabelKey = (id: FormFieldId) => `address-input.inputLabel.${id}` as const;

  readonly getErrorLabelKey = (id: FormFieldId) => `address-input.inputErrorLabel.${id}` as const;

  readonly getZipCodeErrorLabelKey = (id: FormFieldId) => {
    const control = this.formGroup.get('zipCode');
    if (!control || !control.errors?.['zipAvailability']) return this.getErrorLabelKey(id);

    return `address-input.inputErrorLabel.zipCodeAvailability` as const;
  };

  readonly typeaheadPosition = (options: Partial<Options>): Partial<Options> => {
    const context = this.inputContext();
    if (context && this.getIsInvalid(context)) {
      return {
        ...options,
        modifiers: [
          ...(options.modifiers || []),
          {
            name: 'offset',
            options: {
              offset: [0, 15],
            },
          },
        ],
      };
    }
    return options;
  };

  getColumnClass(first: boolean, last: boolean, isRowFirst: boolean, isRowLast: boolean): string {
    if (last && isRowFirst) {
      return 'col-md-8';
    }

    if (first && isRowFirst) {
      return 'col-md-4';
    }

    if (first && isRowLast) {
      return 'col-md-12';
    }

    return 'col-md-6';
  }

  readonly reloadPage = () => {
    window.location.reload();
  };

  isSearchableField(fieldId: FormFieldId) {
    return searchableFields.includes(fieldId as SearchableFieldId);
  }

  // Form validation helpers
  getIsInvalid(controlName: FormFieldId) {
    const control = this.formGroup.get(controlName);
    return !!(control?.errors && control.invalid && (control.dirty || control.touched));
  }

  getIsValid(controlName: FormFieldId) {
    const control = this.formGroup.get(controlName);
    const isValid = control.valid && this.validatedFields.has(controlName) && (control.dirty || control.touched);

    if (controlName !== 'door') return isValid;

    return isValid && this.formatter(control.value, 'door')?.trim()?.length;
  }

  private handleZipCodeChanges() {
    const control = this.formGroup.get('zipCode');

    control?.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged(),
        tap(_ => {
          // When zipCode is cleared or becomes invalid, clear location
          if (control.invalid) {
            const locationControl = this.formGroup.get('location');
            if (locationControl && locationControl.value) {
              this.clearFollowingInputs('location');
            }
          }
        }),
      )
      .subscribe();
  }

  private handlePreselectedOlav() {
    const selectedOlav = this.selectedOlav();
    if (!selectedOlav || this.hasHandledPreselectedOlav) return;

    this.hasHandledPreselectedOlav = true;

    const item: ISolrDocList = {
      docs: [selectedOlav],
      numFound: 1,
      start: 0,
    } as ISolrDocList;

    this.fieldIDs.flat().forEach(id => {
      this.setContext(id);
      this.onSelectItem({ item } as NgbTypeaheadSelectItemEvent<ISolrDocList>);
    });
  }

  private handlePredefinedOlav() {
    const predefinedKey = this.predefinedOlavKey();
    if (!predefinedKey) return;

    this.addressSearchLogic
      .searchPredefinedOlavKey(predefinedKey, this.processType())
      .pipe(
        filter(entry => !!entry),
        tap(item => {
          this.fieldIDs.flat().forEach(id => {
            this.setContext(id);
            this.onSelectItem({ item } as NgbTypeaheadSelectItemEvent<ISolrDocList>);
          });
        }),
        catchError(() => {
          this.showError.set(true);
          return of(null);
        }),
      )
      .subscribe();
  }

  // Add method to check if address input is complete and valid
  private checkAddressCompletion() {
    const selectedOlav = this.selectedOlav();

    // Always emit false if no selected address
    if (!selectedOlav) {
      this.addressInputCompleted.emit(false);
      return;
    }

    const requiredFields: FormFieldId[] = ['zipCode', 'street', 'building'];
    const doorControl = this.formGroup.get('door');

    // Add door to required fields if it's enabled
    if (doorControl?.enabled) {
      requiredFields.push('door');
    }

    // Simple check: all required fields must be enabled, validated, and valid
    const allFieldsValid = requiredFields.every(fieldId => {
      const control = this.formGroup.get(fieldId);
      return control?.enabled && control?.valid && this.validatedFields.has(fieldId);
    });

    // Reuse existing door logic: if door is not enabled, check if it should be
    const doorHandledCorrectly = doorControl?.enabled || !this.shouldDoorBeEnabled(selectedOlav);

    const isComplete = allFieldsValid && doorHandledCorrectly;

    // Always emit the current state - true if complete, false if not
    this.addressInputCompleted.emit(isComplete);
  }

  private shouldDoorBeEnabled(selectedOlav: IOlavEntry) {
    // Create a mock ISolrDocList to reuse the existing shouldEnableDoor logic
    const mockDocList: ISolrDocList = {
      docs: [selectedOlav],
      numFound: 1,
      start: 0,
    } as ISolrDocList;

    return shouldEnableDoor(mockDocList);
  }

  private getOptionalDoors(items: ISolrDocList[]) {
    const optionalDoor = items
      .flatMap(item => item.docs || [])
      .find(doc => {
        return !doc.tuerNr && doc.gueltigKZ === 'H';
      });

    if (!optionalDoor) return;

    this.optionalDoorEntry.set(optionalDoor);
  }

  private enableNextField() {
    const context = this.inputContext();
    const nextField = fieldFlow[context];

    if (!nextField) return;

    if (nextField === 'door') {
      this.makeIdentCallAfterBuildingSelection()
        .pipe(
          tap(items => {
            const isNoResultsFound = isNoResultsMessage(items);
            const areDoorsAvailable = items.some(item => shouldEnableDoor(item));

            this.getOptionalDoors(items);

            if (!isNoResultsFound && areDoorsAvailable) {
              this.enableAndFocusField(nextField);

              if (nextField === 'door' && this.validatedFields.has('door')) {
                this.addValidatorsAfterSelection('door');
              }
              return;
            }

            // Make sure door is disabled if it shouldn't be enabled
            const doorControl = this.formGroup.get('door');
            if (doorControl?.enabled) {
              doorControl.disable();
              this.removeValidators('door');
            }
          }),
          finalize(() => this.checkAddressCompletion()),
          catchError(() => {
            this.showError.set(true);
            return of(null);
          }),
        )
        .subscribe();
      return;
    }

    this.enableAndFocusField(nextField);
  }

  private makeIdentCallAfterBuildingSelection() {
    return this.addressSearchLogic.search({
      value: {
        olavGba: this.selectedOlav()?.gebaeudeKeyIdent,
        street: this.selectedOlav()?.strasse,
        building: this.selectedOlav()?.gebadr1,
      },
      isIdentSearch: true,
      ignoreAvailableZipCodes: this.ignoreAvailableZipCodes(),
    });
  }

  private clearFollowingInputs(startingField: FormFieldId) {
    const fieldSequence: FormFieldId[] = ['zipCode', 'location', 'street', 'building', 'door'];
    const startIndex = fieldSequence.indexOf(startingField);

    if (startIndex === -1) return;
    fieldSequence.slice(startIndex).forEach(fieldName => {
      const control = this.formGroup.get(fieldName);
      if (!control) return;

      control.setValue('', { emitEvent: false });
      control.markAsPristine();
      control.markAsUntouched();

      // Remove validators from cleared fields
      this.removeValidators(fieldName);

      if (!['zipCode', 'location'].includes(fieldName)) {
        control.disable();
      }
    });

    // Clear selectedOlav when clearing location or zipCode
    if (['zipCode', 'location'].includes(startingField)) {
      this.selectedOlav.set(null);
    }

    // Only check completion after clearing is complete with delay
    setTimeout(() => this.checkAddressCompletion(), 0);
  }

  private addValidatorsAfterSelection(fieldId: FormFieldId) {
    const control = this.formGroup.get(fieldId);
    if (!control) return;

    const isDoorEnabled = this.formGroup.get('door')?.enabled ?? false;
    let baseValidators = getBaseValidators({
      fieldId,
      isDoorEnabled,
      ignoreAvailableZipCodes: this.ignoreAvailableZipCodes() ?? false,
      hasOptionalDoorEntry: !!this.optionalDoorEntry(),
    });

    if (fieldId === 'door' && this.optionalDoorEntry()) {
      baseValidators = baseValidators.filter(v => v !== Validators.required);
    }

    const typeaheadValidator = buildTypeaheadValidator({
      fieldId,
      getSelectedOlav: () => this.selectedOlav(),
      isFieldValidated: (field: FormFieldId) => this.validatedFields.has(field),
      skipDoorValidation: fieldId === 'door' && !!this.optionalDoorEntry(),
    });

    control.setValidators([...baseValidators, typeaheadValidator]);
    control.updateValueAndValidity();
  }

  private removeValidators(fieldId: FormFieldId) {
    const control = this.formGroup.get(fieldId);
    if (!control) return;

    control.clearValidators();
    if (fieldId === 'zipCode') {
      control.addValidators(zipCodeValidator(this.ignoreAvailableZipCodes()));
    }
    control.updateValueAndValidity();
    this.validatedFields.delete(fieldId);

    // Check completion after removing validators to emit false if needed
    setTimeout(() => this.checkAddressCompletion(), 0);
  }

  private isValidSearchInput(textInput: string, formValues: typeof this.formGroup.value, fieldId: FormFieldId) {
    if (fieldId === 'zipCode') {
      return (textInput?.match(/\d/g) || []).length >= 3;
    }

    this.checkAddressCompletion();
    return !!textInput?.trim();
  }

  private buildAddressParts(
    formValues: typeof this.formGroup.value,
    textInput: string,
    currentFieldId: FormFieldId,
    useIdentSearch = this.useIdentSearch(),
  ): Partial<AddressParts> {
    const base: Partial<AddressParts> = {
      location: this.formatAddressPart(formValues?.location, 'location'),
      street: this.formatAddressPart(formValues?.street, 'street'),
      building: this.formatAddressPart(formValues?.building, 'building'),
      door: this.formatAddressPart(formValues?.door, 'door'),
      groupField: this.getGroupField(),
    };

    if (useIdentSearch) {
      return {
        olavGba: this.selectedOlav()?.gebaeudeKeyIdent,
        ...base,
      };
    }

    const fieldSequence: FormFieldId[] = ['zipCode', 'location', 'street', 'building', 'door'];
    const currentIndex = fieldSequence.indexOf(currentFieldId);

    const parts: Partial<AddressParts> = {
      groupField: this.getGroupField(),
      location: '*',
    };

    if (currentIndex !== -1) {
      fieldSequence.slice(0, currentIndex + 1).forEach(fieldId => {
        const value = fieldId === currentFieldId ? textInput : formValues?.[fieldId];
        parts[fieldId] = this.formatAddressPart(value, fieldId);
      });
    }

    return parts;
  }

  private getGroupField() {
    const context = this.inputContext();
    if (!context) return null;

    switch (context) {
      case 'zipCode':
        return 'plz';
      case 'street':
        return 'strasse';
      case 'building':
        return 'gebadr1';
      case 'door':
        return 'tuerNr';
      default:
        return null;
    }
  }

  private formatAddressPart(value: any, fieldId: FormFieldId) {
    if (value && typeof value !== 'string') {
      value = this.formatter(value, fieldId);
    }

    if (!value) {
      const type = fieldId === 'zipCode' || fieldId === 'location' ? fieldId : undefined;
      return this.handleEmptyAddressPart(type);
    }

    if (fieldId === 'zipCode') {
      return this.formatZipCode(value);
    }

    if (this.inputContext() === fieldId) {
      return `${value}*`;
    }

    return this.getSelectedOlavValue(fieldId, value);
  }

  private handleEmptyAddressPart(type?: AddressInitialPart) {
    if (type === 'zipCode') {
      const currentValue = this.formGroup.value?.zipCode as string;
      return currentValue?.length ? currentValue.slice(0, 4) : '*';
    }
    if (type === 'location') {
      const currentValue = this.formGroup.value?.location as string;
      return currentValue?.length ? currentValue : '*';
    }
    return '';
  }

  private formatZipCode(value: string) {
    if (this.inputContext() === 'zipCode') {
      return value.slice(0, 4);
    }
    return this.selectedOlav()?.plz || value;
  }

  private getSelectedOlavValue(fieldId: FormFieldId | undefined, value: string) {
    const selectedOlav = this.selectedOlav();
    if (!selectedOlav) {
      return value;
    }

    switch (fieldId) {
      case 'street':
        return selectedOlav.strasse || value;
      case 'building':
        return selectedOlav.gebadr1 || value;
      case 'door':
        return selectedOlav.tuerNr || value;
      case 'location':
        return selectedOlav.gemeinde || value;
      default:
        return value;
    }
  }

  private updateFormWithSelection(item: ISolrDocList) {
    const context = this.inputContext();
    const updates: Record<string, any> = {
      [context]: this.formatter(item),
    };

    if (context === 'zipCode') {
      updates['location'] = item?.docs?.[0]?.gemeinde ?? '';
    }

    this.formGroup.patchValue(updates, { emitEvent: false });
  }

  private enableAndFocusField(fieldId: FormFieldId) {
    const control = this.formGroup.get(fieldId);
    if (fieldId !== 'door' || !this.optionalDoorEntry()) {
      control?.addValidators(Validators.required);
    }
    control?.enable();
    this.focusField(fieldId);
  }

  private focusField(fieldId: FormFieldId | string) {
    setTimeout(() => {
      const element = document.getElementById(fieldId) as HTMLInputElement;
      if (element && !element.disabled) {
        element.focus();
      }
    }, 0);
  }
}
